/**
 * LocalizedDateTimeInput — the date + time editor, timezone- AND locale-correct by
 * construction.
 *
 * Talks UTC ISO-8601 instants on both edges (`value` in, `onChange` out) while the
 * digits the user sees and edits are the wall clock in *their* configured timezone
 * (UserPreferencesContext). A raw `<input type="datetime-local">` has no timezone:
 * it renders and returns bare wall-clock digits that the browser reinterprets in its
 * own local zone, so hand-rolling one (slice the ISO in, `new Date(value)` out)
 * silently shifts the instant by the browser's UTC offset on every round-trip for
 * any non-UTC user — even when they only edited an unrelated field. Route every
 * datetime editor through this component instead of re-deriving that conversion.
 *
 * It is a COMPOSITION of `LocalizedDateInput` + `LocalizedTimeInput` rather than one
 * native control, and that is the second half of "localized". A native
 * `datetime-local` honours the BROWSER's locale and nothing else: it renders
 * `25/08/2026, 08:00` to a user whose PanLuma preference says `mm/dd/yyyy` and a
 * 12-hour clock, offers no way to change that, and steps its minutes one at a time.
 * The two sibling components already read `formatOptions.dateFormat` /
 * `.timeFormat`, so composing them makes ONE editor honour every localization
 * preference the app has — and brings the clock-face picker, whose `minuteStep`
 * defaults to 5 because a 60-position minute ring is unusable and reminders,
 * appointments and cut-offs are never set to the minute.
 *
 * @see shared/utils/datetimeLocal.ts — the UTC↔wall-clock conversion, including DST.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Label } from '../../components/ui/label';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../utils/datetimeLocal';
import { LocalizedDateInput } from './LocalizedDateInput';
import { LocalizedTimeInput } from './LocalizedTimeInput';

export interface LocalizedDateTimeInputProps {
  /** UTC ISO-8601 instant (e.g. `2026-07-14T12:30:00Z`); null/empty when unset. */
  value: string | null | undefined;
  /** Receives a UTC ISO-8601 instant, or null when the field is cleared or incomplete. */
  onChange: (value: string | null) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  /** Minute step offered by the clock-face picker. Defaults to 5. */
  minuteStep?: number;
}

/**
 * The wall clock a date-only entry is completed with.
 *
 * Picking a date and leaving the time blank would keep the whole field incomplete —
 * `onChange(null)` — so the form's submit stays dead with nothing on screen saying
 * why. Seeding a visible, editable 09:00 (the same default the clock-face picker
 * opens on) makes the omission impossible instead of merely reporting it.
 */
const DEFAULT_TIME = '09:00';

/** Do two ISO instants denote the same moment? Null-safe; tolerates differing precision. */
function sameInstant(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return new Date(a).getTime() === new Date(b).getTime();
}

/** The `YYYY-MM-DD` / `HH:mm` halves of a UTC instant, in `timeZone`. Nulls when unset. */
function splitInstant(
  iso: string | null | undefined,
  timeZone: string,
): { date: string | null; time: string | null } {
  const local = toDatetimeLocalValue(iso, timeZone);
  if (!local) return { date: null, time: null };
  return { date: local.slice(0, 10), time: local.slice(11, 16) };
}

/** The two halves back to a UTC instant — null until BOTH are present. */
function joinInstant(
  date: string | null,
  time: string | null,
  timeZone: string,
): string | null {
  if (!date || !time) return null;
  return fromDatetimeLocalValue(`${date}T${time}`, timeZone);
}

export function LocalizedDateTimeInput({
  value,
  onChange,
  label,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  minuteStep = 5,
}: LocalizedDateTimeInputProps) {
  const { t } = useTranslation('common');
  const { timezone } = useUserPreferences();
  const generatedId = React.useId();
  const inputId = id ?? `datetime-input-${generatedId}`;
  const dateId = `${inputId}-date`;
  const timeId = `${inputId}-time`;
  const labelId = `${inputId}-label`;

  // The wall clock currently shown, in the user's timezone, held as its two halves.
  // Local rather than derived from `value` on every render because the pair is
  // incomplete while the user fills the second field, and a purely controlled value
  // would echo that incompleteness back and wipe what they just typed.
  const [parts, setParts] = useState(() => splitInstant(value, timezone));

  // Re-derive when the instant (or the timezone) genuinely changes underneath us —
  // but not when `value` is merely the echo of what the user just entered.
  useEffect(() => {
    if (sameInstant(joinInstant(parts.date, parts.time, timezone), value)) return;
    const next = splitInstant(value, timezone);
    // The two halves carry MINUTE precision, so a stored `:45s` never round-trips to
    // the same instant and the guard above can never be satisfied for one. Comparing
    // the halves is what stops that becoming a render loop: state holds an object, so
    // an unconditional `setParts` would hand React a new identity every pass — where
    // the single string this replaced was bailed out of by `Object.is`.
    if (next.date === parts.date && next.time === parts.time) return;
    setParts(next);
  }, [value, timezone, parts]);

  const commit = useCallback(
    (next: { date: string | null; time: string | null }) => {
      setParts(next);
      onChange(joinInstant(next.date, next.time, timezone));
    },
    [onChange, timezone],
  );

  const handleDateChange = useCallback(
    (date: string | null) =>
      commit({ date, time: date ? parts.time ?? DEFAULT_TIME : parts.time }),
    [commit, parts.time],
  );

  const handleTimeChange = useCallback(
    (time: string | null) =>
      commit({
        // A time with no date means "today, at that time" — the date the user would
        // otherwise have to type to make their own entry count for anything.
        date: time ? parts.date ?? splitInstant(new Date().toISOString(), timezone).date : parts.date,
        time,
      }),
    [commit, parts.date, timezone],
  );

  // The visible label names the GROUP, so each half needs an accessible name of its
  // own — qualified by that label where there is one, since a form can hold several
  // datetime editors and "Date" alone would name them all identically.
  const dateAriaLabel = label ? t('dateTimeInput.dateOf', { field: label }) : t('dateTimeInput.date');
  const timeAriaLabel = label ? t('dateTimeInput.timeOf', { field: label }) : t('dateTimeInput.time');

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label id={labelId} htmlFor={dateId} required={required}>
            {label}
          </Label>
        </div>
      )}
      {/*
        Wrap rather than shrink. Two boxes need ~264px of container to sit side by
        side without the date text running under the calendar button (measured: a
        box below ~116px clips `2026-09-03`), and several callers put this control in
        a column narrower than that — the carrier-schedule dialog's three-up row
        gives it 144px. A `grid-cols-2` there raises no layout error — it just clips,
        silently, on the field the user came to read. `flex-wrap` with a min-width per
        half means the pair stacks in a narrow column and stays side by side
        everywhere else, so a call site cannot get it wrong by being narrow.
      */}
      <div
        className="flex flex-wrap gap-2 [&>*]:min-w-[8rem] [&>*]:flex-1"
        role="group"
        aria-labelledby={label ? labelId : undefined}
        // The seam `src/test-utils/dateTimeInput.ts` drives: a test needs to reach the
        // pair as ONE editor, and neither the group role (shared with every other
        // fieldset) nor the two ids (generated when the caller passes none) identify it.
        data-datetime-input=""
      >
        <LocalizedDateInput
          id={dateId}
          ariaLabel={dateAriaLabel}
          value={parts.date}
          onChange={handleDateChange}
          error={error}
          disabled={disabled}
          size={size}
        />
        <LocalizedTimeInput
          id={timeId}
          ariaLabel={timeAriaLabel}
          value={parts.time}
          onChange={handleTimeChange}
          error={error}
          disabled={disabled}
          size={size}
          step={minuteStep}
        />
      </div>
      {helperText && (
        <p className={cn('text-xs mt-1', error ? 'text-destructive' : 'text-muted-foreground')}>
          {helperText}
        </p>
      )}
    </div>
  );
}
